// The tenant book's current business date and the market calendars, as held by the browser.
//
// A book runs on its business date, which the end-of-day run advances. The browser clock never
// dates a trade, a settlement or a position: it is used only until the first answer from the
// server arrives, and then only to pick the current or next weekday.

export type BookStatus = 'OPEN' | 'EOD_RUNNING' | 'EOD_FAILED';

export interface BusinessDateInfo {
  businessDate: string;
  previousBusinessDate: string;
  nextBusinessDate: string;
  /** The date a new ticket defaults to: the business date, or the next one once end of day has started. */
  tradeDate: string;
  status: BookStatus;
  rollCalendar: string;
  autoRoll: boolean;
  eodTime: string;
  source: string;
  wallClockDate: string;
}

export type Closures = Record<string, string[]>;

const DATE_KEY = 'axiomflow.businessDate';
const CLOSURES_KEY = 'axiomflow.calendarClosures';

let info: BusinessDateInfo | null = null;
let closures: Record<string, Set<string>> = {};
const listeners = new Set<() => void>();

const read = <T,>(key: string): T | null => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

const write = (key: string, value: unknown) => {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage is a convenience across reloads; the in-memory copy is what screens read.
  }
};

const index = (raw: Closures | null): Record<string, Set<string>> => {
  const out: Record<string, Set<string>> = {};
  Object.entries(raw || {}).forEach(([calendar, dates]) => { out[calendar] = new Set(dates); });
  return out;
};

info = read<BusinessDateInfo>(DATE_KEY);
closures = index(read<Closures>(CLOSURES_KEY));

export const setBusinessDateInfo = (next: BusinessDateInfo | null) => {
  const changed = JSON.stringify(next) !== JSON.stringify(info);
  info = next;
  write(DATE_KEY, next);
  if (changed) listeners.forEach(listener => listener());
};

export const setCalendarClosures = (next: Closures | null) => {
  closures = index(next);
  write(CLOSURES_KEY, next);
  listeners.forEach(listener => listener());
};

export const getBusinessDateInfo = (): BusinessDateInfo | null => info;

export const subscribeBusinessDate = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

export const hasCalendar = (calendar: string): boolean => !!closures[calendar];

const pad = (n: number) => String(n).padStart(2, '0');

export const toIso = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** Parses YYYY-MM-DD as a calendar date, with no timezone shift. */
export const fromIso = (iso: string): Date => {
  const [y, m, d] = iso.split('-').map(part => parseInt(part, 10));
  return new Date(y, (m || 1) - 1, d || 1);
};

export const isWeekendIso = (iso: string): boolean => {
  const day = fromIso(iso).getDay();
  return day === 0 || day === 6;
};

/** A business day on every one of the calendars. With no calendar given, the book's own. */
export const isBusinessDayOn = (iso: string, calendars?: string[]): boolean => {
  if (!iso || isWeekendIso(iso)) return false;
  const codes = calendars && calendars.length ? calendars : [info?.rollCalendar || 'US_BOOK'];
  return !codes.some(code => closures[code]?.has(iso));
};

const step = (iso: string, days: number): string => {
  const date = fromIso(iso);
  date.setDate(date.getDate() + days);
  return toIso(date);
};

export const onOrAfter = (iso: string, calendars?: string[]): string => {
  let day = iso;
  for (let guard = 0; guard < 60 && !isBusinessDayOn(day, calendars); guard++) day = step(day, 1);
  return day;
};

export const onOrBefore = (iso: string, calendars?: string[]): string => {
  let day = iso;
  for (let guard = 0; guard < 60 && !isBusinessDayOn(day, calendars); guard++) day = step(day, -1);
  return day;
};

/** Moves by business days; zero returns the date itself, or the next business day if it is not one. */
export const addBusinessDays = (iso: string, days: number, calendars?: string[]): string => {
  if (days === 0) return onOrAfter(iso, calendars);
  let day = iso;
  const direction = days > 0 ? 1 : -1;
  for (let moved = 0; moved < Math.abs(days);) {
    day = step(day, direction);
    if (isBusinessDayOn(day, calendars)) moved++;
  }
  return day;
};

/**
 * Calendars that date an instrument. Mirrors InstrumentCalendars on the server, which has the
 * last word when a ticket is saved.
 */
export const calendarsFor = (ticketType?: string | null, currency?: string | null,
  counterCurrency?: string | null, securityType?: string | null): string[] => {
  const ccy = (currency || 'USD').trim().toUpperCase();
  const dollar = ccy === 'USD';
  const kind = (securityType || '').toUpperCase();
  const equity = kind.includes('EQUITY') || kind.includes('ETF') || kind.includes('STOCK');
  const out: string[] = [];
  switch ((ticketType || '').toUpperCase()) {
    case 'EQUITY':
      out.push('NYSE');
      break;
    case 'PUBLIC_FI':
      out.push(equity ? 'NYSE' : dollar ? 'SIFMA_US' : ccy);
      break;
    case 'CLO_TRANCHE': case 'CMBS_TRANCHE': case 'RMBS_TRANCHE': case 'ABS_TRANCHE': case 'MSR': case 'REPO':
      out.push(dollar ? 'SIFMA_US' : ccy);
      break;
    case 'LEVERAGED_LOAN':
      out.push(dollar ? 'LSTA_US' : ccy);
      break;
    case 'FUTURES':
      out.push('CME');
      break;
    case 'OPTIONS':
      out.push('NYSE');
      break;
    case 'FX': case 'CCS':
      out.push(ccy, 'USD');
      break;
    case '':
      out.push(equity ? 'NYSE' : kind.includes('LOAN') ? (dollar ? 'LSTA_US' : ccy) : dollar ? 'SIFMA_US' : ccy);
      break;
    default:
      out.push(ccy);
  }
  if (counterCurrency && counterCurrency.trim()) out.push(counterCurrency.trim().toUpperCase());
  return Array.from(new Set(out));
};

/** The book's business date. Before the server has answered: the current or next weekday. */
export const currentBusinessDate = (wallClockIso: string): string =>
  info?.businessDate || onOrAfter(wallClockIso);

/** The date a new ticket defaults to. */
export const defaultTradeDate = (wallClockIso: string, calendars?: string[]): string => {
  const base = info?.tradeDate || onOrAfter(wallClockIso);
  return calendars && calendars.length ? onOrAfter(base, calendars) : base;
};
